import { useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@munawb/hooks';
import { fetchExecutiveDashboard } from '@munawb/supabase';
import { supabase } from '@munawb/supabase';
import type { ExecutiveDashboardResponse } from '@munawb/types';
import { RISK_LEVEL_COLORS, formatDate } from '../lib/utils';

type DatePreset = 'today' | 'week' | 'month' | 'quarter' | 'half' | 'year' | 'custom';

interface DateRange {
  start: string | null;
  end:   string | null;
}

function getPresetRange(preset: Exclude<DatePreset, 'custom'>): DateRange {
  const now   = new Date();
  const today = now.toISOString().slice(0, 10);
  switch (preset) {
    case 'today':
      return { start: today, end: today };
    case 'week': {
      const d = new Date(now); d.setDate(d.getDate() - 6);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      const d = new Date(now.getFullYear(), q * 3, 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'half': {
      const m = now.getMonth() < 6 ? 0 : 6;
      const d = new Date(now.getFullYear(), m, 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
    case 'year': {
      const d = new Date(now.getFullYear(), 0, 1);
      return { start: d.toISOString().slice(0, 10), end: today };
    }
  }
}

const PRESET_LABELS: Record<Exclude<DatePreset, 'custom'>, string> = {
  today:   'اليوم',
  week:    'أسبوع',
  month:   'شهر',
  quarter: 'ربع سنوي',
  half:    'نصف سنوي',
  year:    'سنوي',
};

/* ═══════════════════════════════════════════════════════════════════════════
   ExecutivePage
═══════════════════════════════════════════════════════════════════════════ */

export default function ExecutivePage() {
  const { profile }  = useAuthStore();
  const qc           = useQueryClient();

  const [activePreset, setActivePreset] = useState<DatePreset>('today');
  const [dateRange,    setDateRange]    = useState<DateRange>(getPresetRange('today'));
  const [customStart,  setCustomStart]  = useState('');
  const [customEnd,    setCustomEnd]    = useState('');
  const [customError,  setCustomError]  = useState('');
  const [darkMode,     setDarkMode]     = useState(() => document.documentElement.classList.contains('dark'));
  const [showNotifs,   setShowNotifs]   = useState(false);
  const [showLogout,   setShowLogout]   = useState(false);
  const [showProfile,  setShowProfile]  = useState(false);
  const [riskModalLevel, setRiskModalLevel] = useState<string | null>(null);
  const [deptModalName,  setDeptModalName]  = useState<string | null>(null);
  const [execDetailId,   setExecDetailId]   = useState<string | null>(null);

  /* ─── Dark mode ── */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('munawb-dark', darkMode ? '1' : '0');
  }, [darkMode]);

  /* ─── Date presets ─────────────────────────────────────────────────────── */
  const applyPreset = useCallback((preset: Exclude<DatePreset, 'custom'>) => {
    setActivePreset(preset);
    setDateRange(getPresetRange(preset));
    setCustomError('');
  }, []);

  const applyCustom = useCallback(() => {
    if (!customStart || !customEnd) { setCustomError('يرجى تحديد تاريخ البداية والنهاية'); return; }
    if (customEnd < customStart)    { setCustomError('تاريخ النهاية يجب أن يكون بعد البداية'); return; }
    setCustomError('');
    setActivePreset('custom');
    setDateRange({ start: customStart, end: customEnd });
  }, [customStart, customEnd]);

  /* ─── Main dashboard query ─────────────────────────────────────────────── */
  const { data, isLoading, isError, refetch } = useQuery<ExecutiveDashboardResponse>({
    queryKey: ['executive-dashboard', dateRange.start, dateRange.end],
    queryFn:  () => fetchExecutiveDashboard(dateRange.start ?? undefined, dateRange.end ?? undefined),
    staleTime: 5 * 60_000,
  });

  /* ─── Auto-refresh every 5 minutes ─────────────────────────────────────── */
  useEffect(() => {
    const id = setInterval(() => refetch(), 5 * 60_000);
    return () => clearInterval(id);
  }, [refetch]);

  /* ─── Notifications ──────────────────────────────────────────────────────── */
  const { data: notifications = [] } = useQuery({
    queryKey: ['executive-notifications'],
    queryFn: async () => {
      const { data: notifData, error } = await supabase
        .from('notifications')
        .select('*')
        .or(`target_user.eq.${profile?.id}`)
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) return [];
      return notifData ?? [];
    },
    enabled: !!profile,
  });

  const markAllRead = async () => {
    await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('target_user', profile?.id)
      .eq('is_read', false);
    qc.invalidateQueries({ queryKey: ['executive-notifications'] });
  };

  const handleSignOut = async () => {
    setShowLogout(false);
    await supabase.auth.signOut();
  };

  /* ─── Print executive report ─────────────────────────────────────────── */
  const printExecutiveReport = () => {
    if (!data || !kpis) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const dateLabel = dateRange.start === dateRange.end
      ? (dateRange.start ?? '')
      : `${dateRange.start ?? ''} — ${dateRange.end ?? ''}`;
    const deptRows = (data.dept_performance ?? []).map(d => `
      <tr>
        <td>${d.dept_name}</td>
        <td style="text-align:center">${d.open}</td>
        <td style="text-align:center">${d.escalated}</td>
        <td style="text-align:center;color:${d.breached > 0 ? '#dc2626' : '#16a34a'}">${d.breached}</td>
      </tr>`).join('');
    const stuckRows = (data.stuck_events ?? []).map(ev => `
      <tr>
        <td>${ev.event_id}</td>
        <td>${ev.dept}</td>
        <td>${ev.status}</td>
        <td>${ev.risk}</td>
        <td style="text-align:center;color:#dc2626">${Math.round(ev.elapsed_hours)} ساعة</td>
      </tr>`).join('');
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8">
      <title>تقرير القيادة التنفيذية — ${dateLabel}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');
        * { font-family: 'Tajawal', sans-serif; margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #fff; color: #0f172a; padding: 32px; font-size: 13px; }
        .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #7c3aed; padding-bottom: 16px; margin-bottom: 24px; }
        .header h1 { font-size: 22px; font-weight: 800; color: #1e1b4b; }
        .header .meta { font-size: 11px; color: #64748b; text-align: left; }
        .section-title { font-size: 14px; font-weight: 800; color: #1e1b4b; margin: 20px 0 10px; border-right: 4px solid #7c3aed; padding-right: 8px; }
        .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
        .kpi-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; text-align: center; }
        .kpi-card .val { font-size: 28px; font-weight: 800; }
        .kpi-card .lbl { font-size: 11px; color: #64748b; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #f1f5f9; font-weight: 800; padding: 8px 10px; font-size: 11px; text-align: right; border: 1px solid #e2e8f0; }
        td { padding: 7px 10px; border: 1px solid #e2e8f0; font-size: 12px; }
        tr:nth-child(even) td { background: #f8fafc; }
        .footer { margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 12px; text-align: center; font-size: 11px; color: #94a3b8; }
        @page { size: A4 landscape; margin: 15mm; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <div class="header">
        <div>
          <h1>🏛 تقرير لوحة القيادة التنفيذية</h1>
          <div style="font-size:12px;color:#64748b;margin-top:4px">الفترة: ${dateLabel}</div>
        </div>
        <div class="meta">
          <div>أُنشئ بواسطة: ${profile?.name ?? ''}</div>
          <div>${new Date().toLocaleString('ar-SA')}</div>
        </div>
      </div>

      <div class="section-title">مؤشرات الأداء الرئيسية (KPI)</div>
      <div class="kpi-grid">
        <div class="kpi-card"><div class="val" style="color:#3b82f6">${kpis.total_open}</div><div class="lbl">مفتوح</div></div>
        <div class="kpi-card"><div class="val" style="color:#ef4444">${kpis.total_escalated}</div><div class="lbl">مصعّد</div></div>
        <div class="kpi-card"><div class="val" style="color:#10b981">${kpis.total_done}</div><div class="lbl">مغلق</div></div>
        <div class="kpi-card"><div class="val" style="color:#7c3aed">${kpis.compliance_rate}%</div><div class="lbl">نسبة الامتثال SLA</div></div>
      </div>
      <div class="kpi-grid">
        <div class="kpi-card"><div class="val" style="color:#dc2626">${kpis.critical_risk}</div><div class="lbl">خطورة حرجة</div></div>
        <div class="kpi-card"><div class="val" style="color:#f97316">${kpis.high_risk}</div><div class="lbl">خطورة عالية</div></div>
        <div class="kpi-card"><div class="val" style="color:#dc2626">${kpis.breached_open}</div><div class="lbl">تجاوز SLA مفتوح</div></div>
        <div class="kpi-card"><div class="val" style="color:#64748b">${kpis.breached_done}</div><div class="lbl">أُغلق بعد تجاوز SLA</div></div>
      </div>

      <div class="section-title">كفاءة الأقسام والالتزام</div>
      <table>
        <thead><tr><th>القسم</th><th>مفتوح</th><th>مصعّد</th><th>تجاوز SLA</th></tr></thead>
        <tbody>${deptRows || '<tr><td colspan="4" style="text-align:center">لا توجد بيانات</td></tr>'}</tbody>
      </table>

      ${stuckRows ? `<div class="section-title">قائمة الإنذار المبكر (تجاوزت 48 ساعة)</div>
      <table>
        <thead><tr><th>رقم الحدث</th><th>القسم</th><th>الحالة</th><th>الخطورة</th><th>الوقت المنقضي</th></tr></thead>
        <tbody>${stuckRows}</tbody>
      </table>` : ''}

      <div class="footer">نظام مُناوب — تقرير القيادة التنفيذية — ${new Date().toLocaleDateString('ar-SA')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const kpis = data?.kpis;

  /* ─── SLA Ring calculation ─────────────────────────────────────────────── */
  const compliancePct = kpis?.compliance_rate ?? 0;
  const circumference = 2 * Math.PI * 72; // r=72
  const dashOffset    = circumference - (circumference * compliancePct) / 100;

  /* ─── Build chart data from dept_performance ───────────────────────────── */
  const deptPerfEntries = (data?.dept_performance ?? []).slice(0, 8);
  const maxDeptOpen     = Math.max(...deptPerfEntries.map(d => d.open), 1);

  /* ════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950" dir="rtl">

      {/* ── Header ── */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-10 h-10 rounded-xl" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <div>
              <h1 className="font-black text-lg text-slate-800 dark:text-white flex items-center gap-2">
                لوحة القيادة
                <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-[10px] px-2 py-0.5 rounded-full font-black border border-purple-200 dark:border-purple-800 uppercase">Executive</span>
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">غرفة عمليات القيادة التنفيذية</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200 hidden sm:block">{profile?.name}</span>
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifs(v => !v)}
                className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center hover:scale-110 transition border border-blue-100 dark:border-blue-900 text-sm relative"
              >
                🔔
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] font-black flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div className="absolute top-12 left-0 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                    <span className="font-black text-sm text-slate-800 dark:text-white">مركز التنبيهات</span>
                    {notifications.length > 0 && (
                      <button onClick={markAllRead} className="text-[10px] font-black text-blue-600 hover:text-blue-800">تعيين كمقروء</button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                    {notifications.length === 0 ? (
                      <p className="text-center text-slate-400 text-xs py-6">لا توجد إشعارات جديدة</p>
                    ) : notifications.map((n: any) => (
                      <div
                        key={n.id}
                        onClick={() => { if (n.related_incident_id) { setExecDetailId(n.related_incident_id); setShowNotifs(false); } }}
                        className={`p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-xs font-bold text-slate-700 dark:text-slate-300 ${n.related_incident_id ? 'cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors' : ''}`}
                      >
                        {n.message}
                        <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(n.created_at)}</p>
                        {n.related_incident_id && <p className="text-[10px] text-blue-500 mt-0.5 font-black">اضغط لعرض الحدث ←</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowProfile(true)}
              title="الملف الشخصي"
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:scale-110 transition border border-slate-200 dark:border-slate-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A9 9 0 1118.88 6.196M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={() => refetch()}
              title="تحديث"
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:scale-110 transition border border-slate-200 dark:border-slate-700 text-sm"
            >
              🔄
            </button>
            {data && kpis && (
              <button
                onClick={printExecutiveReport}
                title="طباعة التقرير"
                className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:scale-110 transition border border-slate-200 dark:border-slate-700 text-sm"
              >
                🖨️
              </button>
            )}
            <button
              onClick={() => setDarkMode(d => !d)}
              className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-yellow-400 hover:scale-110 transition border border-slate-200 dark:border-slate-700 text-sm"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={() => setShowLogout(true)} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* ── Date Filter Bar ── */}
        <div className="glass-panel rounded-2xl p-4 border-r-4 border-purple-500">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-black text-sm text-slate-700 dark:text-slate-300 shrink-0">الفترة الزمنية:</span>
            {(Object.entries(PRESET_LABELS) as [Exclude<DatePreset,'custom'>, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className={`px-3 py-1.5 rounded-xl font-black text-xs transition-all ${
                  activePreset === key
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-purple-300'
                }`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setActivePreset('custom')}
              className={`px-3 py-1.5 rounded-xl font-black text-xs transition-all ${
                activePreset === 'custom'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-purple-300'
              }`}
            >
              مخصص
            </button>
          </div>

          {activePreset === 'custom' && (
            <div className="flex flex-wrap items-end gap-3 pt-3 mt-3 border-t border-slate-200 dark:border-slate-700">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">من</label>
                <input type="date" className="input-field py-2 text-sm" dir="ltr" value={customStart} onChange={e => setCustomStart(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">إلى</label>
                <input type="date" className="input-field py-2 text-sm" dir="ltr" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
              </div>
              <button onClick={applyCustom} className="btn-primary text-sm px-5 py-2">تطبيق</button>
              {customError && <p className="text-red-500 text-xs font-bold w-full">{customError}</p>}
            </div>
          )}
        </div>

        {/* ── Loading / Error ── */}
        {isLoading && (
          <div className="glass-panel rounded-2xl p-10 text-center">
            <span className="spinner border-purple-200 border-t-purple-600" />
            <p className="text-sm text-slate-400 font-bold mt-3">جارٍ تحميل بيانات القيادة...</p>
          </div>
        )}
        {isError && (
          <div className="glass-panel rounded-2xl p-8 text-center">
            <p className="text-red-500 font-bold text-sm mb-3">تعذّر تحميل لوحة القيادة</p>
            <button onClick={() => refetch()} className="btn-primary text-sm">إعادة المحاولة</button>
          </div>
        )}

        {!isLoading && data && kpis && (
          <>
            {/* ── SLA Compliance Radar ── */}
            <div className="glass-panel rounded-2xl p-6 border-t-4 border-teal-500">
              <div className="flex justify-between items-center mb-5">
                <h3 className="font-black text-lg text-teal-700 dark:text-teal-400">📡 رادار حوكمة الامتثال (SLA Compliance)</h3>
                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full">
                  {new Date(data.generated_at).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">

                {/* Ring SVG */}
                <div className="flex justify-center">
                  <div className="relative w-36 h-36">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
                      <circle cx="80" cy="80" r="72" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-100 dark:text-slate-800" />
                      <circle
                        cx="80" cy="80" r="72"
                        stroke="currentColor" strokeWidth="12" fill="transparent"
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                        className={`transition-all duration-1000 ${compliancePct >= 80 ? 'text-teal-500' : compliancePct >= 60 ? 'text-yellow-500' : 'text-red-500'}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-black text-slate-800 dark:text-white">{compliancePct}%</span>
                      <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase">نسبة الامتثال</span>
                    </div>
                  </div>
                </div>

                {/* KPI 2-col grid */}
                <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">البلاغات المفتوحة</p>
                    <p className="text-4xl font-black text-slate-800 dark:text-white">{kpis.total_open}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="text-[10px] font-bold text-orange-600 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full border border-orange-200 dark:border-orange-900">
                        مصعد: {kpis.total_escalated}
                      </span>
                      <span className="text-[10px] font-bold text-teal-600 bg-teal-50 dark:bg-teal-900/20 px-2 py-0.5 rounded-full border border-teal-200 dark:border-teal-900">
                        مغلق: {kpis.total_done}
                      </span>
                    </div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/10 p-5 rounded-2xl border border-red-200 dark:border-red-900/50">
                    <p className="text-xs font-bold text-red-600 dark:text-red-400 mb-1">متجاوزة المعيار الآن</p>
                    <p className="text-4xl font-black text-red-600 dark:text-red-500">{kpis.breached_open}</p>
                    <p className="text-[10px] text-red-400 font-bold mt-2">تاريخي: {kpis.breached_done} أُغلق بعد التجاوز</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Risk Heatmap ── */}
            <div className="space-y-3">
              <h3 className="font-black text-base text-slate-700 dark:text-slate-300 flex items-center gap-2">
                🎯 خريطة المخاطر النشطة (Heatmap)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    level: 'حرج',
                    count: kpis.critical_risk,
                    color: 'border-r-4 border-red-500 bg-red-50 dark:bg-red-900/10',
                    badge: 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900',
                    text:  'text-red-600 dark:text-red-500',
                    desc:  'يتطلب تدخلاً فورياً من الإدارة',
                    icon:  '☢️',
                  },
                  {
                    level: 'عالي',
                    count: kpis.high_risk,
                    color: 'border-r-4 border-orange-500 bg-orange-50 dark:bg-orange-900/10',
                    badge: 'bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-900',
                    text:  'text-orange-600 dark:text-orange-500',
                    desc:  'تهديدات تشغيلية قيد المتابعة',
                    icon:  '⚠️',
                  },
                  {
                    level: 'متوسط',
                    count: (data.dept_performance ?? []).reduce((s, d) => s + d.open, 0) - kpis.critical_risk - kpis.high_risk,
                    color: 'border-r-4 border-blue-500 bg-blue-50 dark:bg-blue-900/10',
                    badge: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-900',
                    text:  'text-blue-600 dark:text-blue-500',
                    desc:  'بلاغات تشغيلية اعتيادية',
                    icon:  '📂',
                  },
                ].map(item => (
                  <div
                    key={item.level}
                    onClick={() => setRiskModalLevel(item.level)}
                    className={`glass-panel p-6 rounded-2xl ${item.color} relative overflow-hidden group hover:-translate-y-1 transition-transform cursor-pointer`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className={`text-xs font-black px-2 py-1 rounded border ${item.badge}`}>
                        مستوى الخطورة: {item.level}
                      </span>
                      <span className="text-xl">{item.icon}</span>
                    </div>
                    <p className={`text-5xl font-black ${item.text}`}>{Math.max(0, item.count)}</p>
                    <p className="text-[10px] text-slate-500 mt-2 font-bold uppercase">{item.desc}</p>
                    <p className="text-[10px] text-slate-400 mt-1 font-bold">اضغط لعرض التفاصيل ←</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Charts Row ── */}
            <div className="space-y-3">
              <h3 className="font-black text-base text-slate-700 dark:text-slate-300 flex items-center gap-2">
                📊 التحليلات البيانية
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Status distribution */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4 uppercase tracking-widest">توزيع حالات الأحداث</p>
                  <div className="space-y-2.5">
                    {[
                      { label: 'مفتوح',         value: kpis.total_open,      color: '#3b82f6' },
                      { label: 'مصعد',           value: kpis.total_escalated, color: '#ef4444' },
                      { label: 'مغلق',           value: kpis.total_done,      color: '#10b981' },
                    ].map(item => {
                      const total = kpis.total_open + kpis.total_escalated + kpis.total_done;
                      const pct   = total > 0 ? Math.round((item.value / total) * 100) : 0;
                      return (
                        <div key={item.label}>
                          <div className="flex justify-between text-xs font-bold mb-0.5">
                            <span className="text-slate-600 dark:text-slate-300">{item.label}</span>
                            <span className="text-slate-400">{item.value} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Risk distribution */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4 uppercase tracking-widest">توزيع مستويات الخطورة</p>
                  <div className="space-y-2.5">
                    {[
                      { label: 'حرج',   value: kpis.critical_risk,                                                                    color: '#7c3aed' },
                      { label: 'عالي',  value: kpis.high_risk,                                                                         color: '#f97316' },
                      { label: 'باقي',  value: Math.max(0, kpis.total_open - kpis.critical_risk - kpis.high_risk),                     color: '#3b82f6' },
                    ].map(item => {
                      const pct = kpis.total_open > 0 ? Math.round((item.value / kpis.total_open) * 100) : 0;
                      return (
                        <div key={item.label}>
                          <div className="flex justify-between text-xs font-bold mb-0.5">
                            <span className="text-slate-600 dark:text-slate-300">{item.label}</span>
                            <span className="text-slate-400">{item.value} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RCA Governance */}
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4 uppercase tracking-widest">حوكمة الجودة (RCA)</p>
                  <div className="space-y-2.5">
                    {[
                      { label: 'مكتمل',  value: data.rca_completed_events?.length ?? 0, color: '#10b981' },
                      { label: 'معلق',   value: data.rca_pending_events?.length   ?? 0, color: '#f59e0b' },
                    ].map(item => {
                      const total = (data.rca_completed_events?.length ?? 0) + (data.rca_pending_events?.length ?? 0);
                      const pct   = total > 0 ? Math.round((item.value / total) * 100) : 0;
                      return (
                        <div key={item.label}>
                          <div className="flex justify-between text-xs font-bold mb-0.5">
                            <span className="text-slate-600 dark:text-slate-300">RCA {item.label}</span>
                            <span className="text-slate-400">{item.value} ({pct}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Dept performance bar chart */}
              {deptPerfEntries.length > 0 && (
                <div className="glass-panel rounded-2xl p-5">
                  <p className="text-xs font-black text-slate-500 dark:text-slate-400 mb-4 uppercase tracking-widest">أداء الأقسام (بلاغات مفتوحة / متجاوزة)</p>
                  <div className="space-y-2.5">
                    {deptPerfEntries.map(dept => {
                      const pct = Math.round((dept.open / maxDeptOpen) * 100);
                      return (
                        <div key={dept.dept_name}>
                          <div className="flex justify-between text-xs font-bold mb-0.5">
                            <span className="text-slate-600 dark:text-slate-300 truncate max-w-[120px]">{dept.dept_name}</span>
                            <div className="flex gap-3 text-slate-400">
                              <span className="text-blue-600 font-black">{dept.open} مفتوح</span>
                              {dept.breached > 0 && <span className="text-red-600 font-black">{dept.breached} متجاوز</span>}
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ── Stuck Events + Dept Performance Table ── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

              {/* Stuck Events */}
              <div className="glass-panel rounded-2xl p-6 border-t-4 border-red-500 flex flex-col">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-slate-800 mb-4">
                  <h3 className="font-black text-base text-red-600 dark:text-red-400 flex items-center gap-2">
                    🚨 قائمة الإنذار المبكر
                  </h3>
                  <span className="text-[10px] font-bold text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300 px-3 py-1 rounded-full border border-red-200 dark:border-red-800">
                    تجاوزت 48 ساعة
                  </span>
                </div>
                {!data.stuck_events?.length ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-4xl mb-2">✅</p>
                      <p className="text-sm text-slate-400 font-bold">لا توجد أحداث عالقة</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-y-auto max-h-96">
                    {data.stuck_events.map((ev, i) => {
                      const colors = RISK_LEVEL_COLORS[ev.risk] ?? RISK_LEVEL_COLORS['منخفض'];
                      return (
                        <div
                          key={i}
                          onClick={() => ev.id && setExecDetailId(ev.id)}
                          className={`flex items-start justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800 last:border-0 ${ev.id ? 'cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl px-2 -mx-2 transition-colors' : ''}`}
                        >
                          <div>
                            <p className="text-xs font-black text-slate-800 dark:text-white">{ev.event_id}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{ev.dept}</p>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500">{ev.status}</p>
                          </div>
                          <div className="text-left shrink-0">
                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${colors.badge}`}>{ev.risk}</span>
                            <p className="text-[11px] text-red-500 font-black mt-1">{Math.round(ev.elapsed_hours)} ساعة</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Dept Performance Table */}
              <div className="glass-panel rounded-2xl p-6 border-t-4 border-blue-500 flex flex-col">
                <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-slate-800 mb-4">
                  <h3 className="font-black text-base text-slate-800 dark:text-white flex items-center gap-2">
                    📈 كفاءة الأقسام والالتزام
                  </h3>
                  <span className="text-[10px] font-bold text-blue-700 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-800">
                    الأقسام الأكثر تجاوزاً
                  </span>
                </div>
                {!data.dept_performance?.length ? (
                  <div className="flex-1 flex items-center justify-center">
                    <p className="text-sm text-slate-400 font-bold">لا توجد بيانات أقسام</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto flex-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-800">
                          <th className="pb-2 text-right font-black text-xs text-slate-500 dark:text-slate-400">القسم</th>
                          <th className="pb-2 text-center font-black text-xs text-blue-600">مفتوح</th>
                          <th className="pb-2 text-center font-black text-xs text-orange-600">مصعّد</th>
                          <th className="pb-2 text-center font-black text-xs text-red-600">تجاوز SLA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.dept_performance.map((dept, i) => (
                          <tr
                            key={i}
                            onClick={() => setDeptModalName(dept.dept_name)}
                            className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors cursor-pointer"
                          >
                            <td className="py-2.5 font-bold text-slate-800 dark:text-white text-xs">{dept.dept_name}</td>
                            <td className="py-2.5 text-center font-black text-blue-600">{dept.open}</td>
                            <td className="py-2.5 text-center font-black text-orange-600">{dept.escalated}</td>
                            <td className="py-2.5 text-center">
                              {dept.breached > 0
                                ? <span className="font-black text-red-600">{dept.breached}</span>
                                : <span className="font-black text-green-600">0</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

      </main>

      {/* ── Modals ── */}
      {riskModalLevel && (
        <ExecRiskModal
          riskLevel={riskModalLevel}
          dateRange={dateRange}
          onClose={() => setRiskModalLevel(null)}
          onViewDetail={setExecDetailId}
        />
      )}
      {deptModalName && (
        <ExecDeptModal
          deptName={deptModalName}
          dateRange={dateRange}
          onClose={() => setDeptModalName(null)}
          onViewDetail={setExecDetailId}
        />
      )}
      {execDetailId && (
        <ExecEventDetailModal
          incidentId={execDetailId}
          onClose={() => setExecDetailId(null)}
        />
      )}
      {showProfile && (
        <ExecProfileModal
          profile={profile}
          onClose={() => setShowProfile(false)}
        />
      )}
      {showLogout && (
        <ExecLogoutModal
          onConfirm={handleSignOut}
          onCancel={() => setShowLogout(false)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ExecRiskModal — incidents filtered by risk level
═══════════════════════════════════════════════════════════════════════════ */
function ExecRiskModal({ riskLevel, dateRange, onClose, onViewDetail }: {
  riskLevel: string;
  dateRange: DateRange;
  onClose: () => void;
  onViewDetail: (id: string) => void;
}) {
  const { data: incidents = [], isLoading } = useQuery({
    queryKey: ['exec-risk-incidents', riskLevel, dateRange.start, dateRange.end],
    queryFn: async () => {
      let q = supabase
        .from('incidents')
        .select('*')
        .eq('risk_level', riskLevel)
        .in('status', ['مفتوح', 'مصعد', 'طلب RCA', 'RCA Completed'])
        .order('created_at', { ascending: false });
      if (dateRange.start) q = q.gte('created_at', dateRange.start);
      if (dateRange.end)   q = q.lte('created_at', dateRange.end + 'T23:59:59');
      const { data, error } = await q;
      if (error) return [];
      return data ?? [];
    },
  });

  const riskColors: Record<string, string> = {
    'حرج':    'border-red-500 text-red-600',
    'عالي':   'border-orange-500 text-orange-600',
    'متوسط':  'border-blue-500 text-blue-600',
    'منخفض':  'border-teal-500 text-teal-600',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700"
           onClick={e => e.stopPropagation()}>
        <div className={`px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center border-r-4 ${riskColors[riskLevel] ?? 'border-blue-500 text-blue-600'}`}>
          <div>
            <h3 className="font-black text-base text-slate-800 dark:text-white">الأحداث ذات الخطورة: {riskLevel}</h3>
            <p className="text-xs text-slate-400 font-bold mt-0.5">{incidents.length} حدث</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:bg-red-100 hover:text-red-500 transition text-sm font-black">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="text-center py-10">
              <span className="spinner border-purple-200 border-t-purple-600" />
              <p className="text-sm text-slate-400 font-bold mt-3">جارٍ التحميل...</p>
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-3xl mb-2">✅</p>
              <p className="text-sm text-slate-400 font-bold">لا توجد أحداث بهذا المستوى</p>
            </div>
          ) : incidents.map((inc: any) => (
            <div
              key={inc.id}
              onClick={() => { onViewDetail(inc.id); onClose(); }}
              className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors bg-white dark:bg-slate-800"
            >
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="text-xs font-black text-slate-800 dark:text-white">{inc.event_id ?? inc.id?.slice(0, 8)}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{inc.dept_name}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{inc.description}</p>
                </div>
                <div className="shrink-0 text-left">
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${inc.status === 'مصعد' ? 'border-red-200 bg-red-50 text-red-600' : 'border-blue-200 bg-blue-50 text-blue-600'}`}>{inc.status}</span>
                  <p className="text-[10px] text-slate-400 mt-1">{formatDate(inc.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ExecDeptModal — incidents filtered by department
═══════════════════════════════════════════════════════════════════════════ */
function ExecDeptModal({ deptName, dateRange, onClose, onViewDetail }: {
  deptName: string;
  dateRange: DateRange;
  onClose: () => void;
  onViewDetail: (id: string) => void;
}) {
  const { data: incidents = [], isLoading } = useQuery({
    queryKey: ['exec-dept-incidents', deptName, dateRange.start, dateRange.end],
    queryFn: async () => {
      let q = supabase
        .from('incidents')
        .select('*')
        .eq('dept_name', deptName)
        .order('created_at', { ascending: false });
      if (dateRange.start) q = q.gte('created_at', dateRange.start);
      if (dateRange.end)   q = q.lte('created_at', dateRange.end + 'T23:59:59');
      const { data, error } = await q;
      if (error) return [];
      return data ?? [];
    },
  });

  const totalOpen       = incidents.filter((i: any) => i.status === 'مفتوح').length;
  const totalEscalated  = incidents.filter((i: any) => i.status === 'مصعد').length;
  const totalClosed     = incidents.filter((i: any) => i.status === 'مغلق').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700"
           onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 border-r-4 border-blue-500 flex justify-between items-center">
          <div>
            <h3 className="font-black text-base text-slate-800 dark:text-white">أحداث قسم: {deptName}</h3>
            <div className="flex gap-3 mt-1">
              <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">{totalOpen} مفتوح</span>
              <span className="text-[10px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">{totalEscalated} مصعد</span>
              <span className="text-[10px] font-black text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">{totalClosed} مغلق</span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:bg-red-100 hover:text-red-500 transition text-sm font-black">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="text-center py-10">
              <span className="spinner border-blue-200 border-t-blue-600" />
              <p className="text-sm text-slate-400 font-bold mt-3">جارٍ التحميل...</p>
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-3xl mb-2">📂</p>
              <p className="text-sm text-slate-400 font-bold">لا توجد أحداث لهذا القسم</p>
            </div>
          ) : incidents.map((inc: any) => (
            <div
              key={inc.id}
              onClick={() => { onViewDetail(inc.id); onClose(); }}
              className="p-4 rounded-xl border border-slate-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors bg-white dark:bg-slate-800"
            >
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="text-xs font-black text-slate-800 dark:text-white">{inc.event_id ?? inc.id?.slice(0, 8)}</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{inc.risk_level} — {inc.category}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{inc.description}</p>
                </div>
                <div className="shrink-0 text-left">
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${inc.status === 'مصعد' ? 'border-red-200 bg-red-50 text-red-600' : inc.status === 'مغلق' ? 'border-teal-200 bg-teal-50 text-teal-600' : 'border-blue-200 bg-blue-50 text-blue-600'}`}>{inc.status}</span>
                  <p className="text-[10px] text-slate-400 mt-1">{formatDate(inc.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ExecEventDetailModal — full incident details + timeline
═══════════════════════════════════════════════════════════════════════════ */
function ExecEventDetailModal({ incidentId, onClose }: {
  incidentId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['exec-incident-detail', incidentId],
    queryFn: async () => {
      const [incRes, logsRes] = await Promise.all([
        supabase.from('incidents').select('*').eq('id', incidentId).single(),
        supabase.from('incident_logs').select('*').eq('incident_id', incidentId).order('created_at', { ascending: true }),
      ]);
      return { incident: incRes.data, logs: logsRes.data ?? [] };
    },
  });

  const inc  = data?.incident;
  const logs = data?.logs ?? [];

  const printDetailReport = () => {
    if (!inc) return;
    const logsHTML = logs.map((lg: any) => `
      <tr>
        <td>${formatDate(lg.created_at)}</td>
        <td>${lg.action ?? ''}</td>
        <td>${lg.actor_name ?? ''}</td>
        <td>${lg.note ?? ''}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8">
      <title>تقرير الحدث ${inc.event_id ?? ''}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&display=swap');
        * { font-family: 'Tajawal', sans-serif; margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #fff; color: #0f172a; padding: 32px; font-size: 13px; }
        .header { border-bottom: 3px solid #7c3aed; padding-bottom: 16px; margin-bottom: 24px; }
        .header h1 { font-size: 20px; font-weight: 800; color: #1e1b4b; }
        .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
        .field { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; }
        .field .label { font-size: 10px; font-weight: 800; color: #94a3b8; margin-bottom: 4px; }
        .field .val { font-size: 13px; font-weight: 700; }
        .section-title { font-size: 14px; font-weight: 800; color: #1e1b4b; margin: 20px 0 10px; border-right: 4px solid #7c3aed; padding-right: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f1f5f9; font-weight: 800; padding: 8px 10px; font-size: 11px; text-align: right; border: 1px solid #e2e8f0; }
        td { padding: 7px 10px; border: 1px solid #e2e8f0; font-size: 12px; }
        tr:nth-child(even) td { background: #f8fafc; }
        .footer { margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 12px; text-align: center; font-size: 11px; color: #94a3b8; }
        @page { size: A4 portrait; margin: 15mm; }
      </style></head><body>
      <div class="header">
        <h1>📋 تقرير الحدث التشغيلي</h1>
        <p style="font-size:12px;color:#64748b;margin-top:4px">${inc.event_id ?? ''} — ${inc.dept_name ?? ''}</p>
      </div>
      <div class="grid2">
        <div class="field"><div class="label">رقم الحدث</div><div class="val">${inc.event_id ?? ''}</div></div>
        <div class="field"><div class="label">الحالة</div><div class="val">${inc.status ?? ''}</div></div>
        <div class="field"><div class="label">القسم</div><div class="val">${inc.dept_name ?? ''}</div></div>
        <div class="field"><div class="label">مستوى الخطورة</div><div class="val">${inc.risk_level ?? ''}</div></div>
        <div class="field"><div class="label">التصنيف</div><div class="val">${inc.category ?? ''}</div></div>
        <div class="field"><div class="label">تاريخ الفتح</div><div class="val">${formatDate(inc.created_at)}</div></div>
      </div>
      <div class="section-title">وصف الحدث</div>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;margin-bottom:20px;line-height:1.8">${inc.description ?? ''}</div>
      ${logsHTML ? `<div class="section-title">سجل الأحداث والإجراءات</div>
      <table>
        <thead><tr><th>التوقيت</th><th>الإجراء</th><th>المنفذ</th><th>الملاحظة</th></tr></thead>
        <tbody>${logsHTML}</tbody>
      </table>` : ''}
      <div class="footer">نظام مُناوب — تقرير تنفيذي — ${new Date().toLocaleDateString('ar-SA')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700"
           onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 border-r-4 border-purple-500 flex justify-between items-center">
          <div>
            <h3 className="font-black text-base text-slate-800 dark:text-white flex items-center gap-2">
              📋 تفاصيل الحدث
              {inc && <span className="text-[10px] text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded-full border border-purple-200 dark:border-purple-800 font-black">{inc.event_id}</span>}
            </h3>
            {inc && <p className="text-xs text-slate-400 font-bold mt-0.5">{inc.dept_name} · {inc.status}</p>}
          </div>
          <div className="flex items-center gap-2">
            {inc && (
              <button onClick={printDetailReport} className="btn-secondary text-xs px-3 py-1.5">🖨️ طباعة</button>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:bg-red-100 hover:text-red-500 transition text-sm font-black">✕</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="text-center py-10">
              <span className="spinner border-purple-200 border-t-purple-600" />
              <p className="text-sm text-slate-400 font-bold mt-3">جارٍ التحميل...</p>
            </div>
          ) : !inc ? (
            <p className="text-center text-red-500 font-bold py-10">تعذّر تحميل بيانات الحدث</p>
          ) : (
            <div className="space-y-4">
              {/* Incident info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'رقم الحدث',     val: inc.event_id },
                  { label: 'الحالة',          val: inc.status },
                  { label: 'القسم',           val: inc.dept_name },
                  { label: 'مستوى الخطورة',  val: inc.risk_level },
                  { label: 'التصنيف',        val: inc.category },
                  { label: 'تاريخ الفتح',    val: formatDate(inc.created_at) },
                ].map(f => (
                  <div key={f.label} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border border-slate-100 dark:border-slate-700">
                    <p className="text-[10px] font-bold text-slate-400 mb-1">{f.label}</p>
                    <p className="text-xs font-black text-slate-800 dark:text-white">{f.val ?? '—'}</p>
                  </div>
                ))}
              </div>
              {/* Description */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-100 dark:border-slate-700">
                <p className="text-[10px] font-bold text-slate-400 mb-2">وصف الحدث</p>
                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{inc.description || 'لا يوجد وصف'}</p>
              </div>
              {/* Log timeline */}
              {logs.length > 0 && (
                <div>
                  <p className="text-xs font-black text-slate-600 dark:text-slate-300 mb-3">سجل الأحداث ({logs.length})</p>
                  <div className="space-y-2">
                    {logs.map((lg: any) => (
                      <div key={lg.id} className="flex gap-3 items-start">
                        <div className="w-2 h-2 rounded-full bg-purple-500 mt-1.5 shrink-0" />
                        <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl p-3 border border-slate-100 dark:border-slate-700">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] font-black text-purple-600 dark:text-purple-400">{lg.action}</span>
                            <span className="text-[10px] text-slate-400">{formatDate(lg.created_at)}</span>
                          </div>
                          {lg.note && <p className="text-[11px] text-slate-600 dark:text-slate-400">{lg.note}</p>}
                          {lg.actor_name && <p className="text-[10px] text-slate-400 mt-0.5">بواسطة: {lg.actor_name}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ExecProfileModal
═══════════════════════════════════════════════════════════════════════════ */
function ExecProfileModal({ profile, onClose }: { profile: any; onClose: () => void }) {
  const [phone,      setPhone]      = useState(profile?.phone ?? '');
  const [newPass,    setNewPass]    = useState('');
  const [notifEmail, setNotifEmail] = useState(profile?.notif_email ?? true);
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState('');

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      const updates: any = { phone, notif_email: notifEmail };
      const { error: profErr } = await supabase.from('profiles').update(updates).eq('id', profile?.id);
      if (profErr) throw profErr;
      if (newPass) {
        const { error: passErr } = await supabase.auth.updateUser({ password: newPass });
        if (passErr) throw passErr;
      }
      setMsg('تم الحفظ بنجاح ✓');
      setNewPass('');
    } catch (e: any) {
      setMsg('حدث خطأ: ' + (e.message ?? ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-200 dark:border-slate-700"
           onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 border-r-4 border-purple-500 flex justify-between items-center">
          <h3 className="font-black text-base text-slate-800 dark:text-white">الملف الشخصي</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:bg-red-100 hover:text-red-500 transition text-sm font-black">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">الاسم</label>
            <input className="input-field py-2 text-sm bg-slate-50" value={profile?.name ?? ''} readOnly />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">رقم الجوال</label>
            <input className="input-field py-2 text-sm" dir="ltr" value={phone} onChange={e => setPhone(e.target.value)} placeholder="05xxxxxxxx" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">كلمة مرور جديدة (اختياري)</label>
            <input type="password" className="input-field py-2 text-sm" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="اتركه فارغاً إن لم ترد التغيير" />
          </div>
          <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 rounded-xl px-4 py-3 border border-slate-100 dark:border-slate-700">
            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">تلقّي الإشعارات بالبريد</span>
            <button
              onClick={() => setNotifEmail((v: boolean) => !v)}
              className={`w-11 h-6 rounded-full transition-all relative ${notifEmail ? 'bg-purple-600' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${notifEmail ? 'right-0.5' : 'left-0.5'}`} />
            </button>
          </div>
          {msg && <p className={`text-xs font-bold text-center ${msg.startsWith('حدث') ? 'text-red-500' : 'text-teal-600'}`}>{msg}</p>}
          <button onClick={save} disabled={saving} className="btn-primary w-full py-2.5 text-sm">
            {saving ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ExecLogoutModal
═══════════════════════════════════════════════════════════════════════════ */
function ExecLogoutModal({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" dir="rtl">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-slate-200 dark:border-slate-700 text-center">
        <div className="text-4xl mb-3">👋</div>
        <h3 className="font-black text-base text-slate-800 dark:text-white mb-2">تأكيد تسجيل الخروج</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold mb-6">هل أنت متأكد من رغبتك في الخروج من لوحة القيادة؟</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 btn-secondary text-sm py-2.5">إلغاء</button>
          <button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black text-sm py-2.5 rounded-xl transition-colors">خروج</button>
        </div>
      </div>
    </div>
  );
}
